import { paymentPluginIkhPay, sendIKHInvoiceMails } from '../api/ikh-payment-plugin.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { BHIKHOrderNr } from './BHIKHOrderNr.js';
import { getProductByID } from '@adornis/buchungsheld-db/api/product.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { getCurrentUserServer, saveEntityData } from '@adornis/bh-util/util.js';
import { getParticipationsByID } from '@adornis/buchungsheld-db/api/participation-helper.js';
import { getParticipationCurrentPaymentServer } from '@adornis/buchungsheld-db/api/participation.js';
import type { PaymentPlugin } from '@adornis/buchungsheld-db/db-plugins.js';
import { BHInvoice } from '@adornis/buchungsheld-db/entities/BHInvoice.js';
import { BundlePrice } from '@adornis/buchungsheld-db/entities/BundlePrice.js';
import { Price } from '@adornis/payments/db/prices.js';
import { IKHInvoiceReceiverEnum } from './invoice-receiver.js';
import { defaultPaymentPlugin } from './default.js';

export const buildIkhInvoiceFileName = (
  orderNr: BHIKHOrderNr,
  lastName: Maybe<string>,
  variant: 'customer' | 'internal',
  correction: boolean,
): string => {
  const safeLastName =
    (lastName ?? '')
      .normalize('NFC')
      .replace(/[^\p{L}\p{Nd}\s-]/gu, '')
      .replace(/\s+/g, ' ')
      .trim() || 'Rechnung';
  return `${orderNr.getOrderNr().replace(/\//g, '-')} ${safeLastName}${variant === 'internal' ? ' intern' : ''}${correction ? ' Korrektur' : ''}.pdf`;
};

@Entity()
export class IKHInvoice extends BHInvoice {
  static override _class = 'IKHInvoice';

  // pdf file path of interal-invoice version for accouning
  @Field(type => String)
  pdfInternalID!: string;

  @DataLoadedField(type => AdornisFile, ({ pdfInternalID }) => pdfInternalID)
  pdfInternal!: AdornisFile;

  @Field(type => String)
  pdfCorrectionID!: string;

  @DataLoadedField(type => AdornisFile, ({ pdfCorrectionID }) => pdfCorrectionID)
  pdfCorrection!: AdornisFile;

  @Field(type => String)
  pdfCorrectionInternalID!: string;

  @DataLoadedField(type => AdornisFile, ({ pdfCorrectionInternalID }) => pdfCorrectionInternalID)
  pdfCorrectionInternal!: AdornisFile;
}

export function selectIkhProductPrice(prices: Price[], bundleID?: string): Maybe<Price> {
  const regularPrice = prices[0];
  if (!bundleID) return regularPrice;
  const bundlePrice = prices.find(price => price instanceof BundlePrice && price.bundleID === bundleID);
  return bundlePrice ?? regularPrice;
}

export const getProductPrice = async (productID: string, userID: string, bundleID?: string) => {
  const product = await getProductByID(productID)({
    prices: { ...selectionSet(() => Price, 2), _id: 1, unitAmount: 1, currency: 1 },
  });
  if (!product) throw new Error('could not find product ' + productID);
  const price = selectIkhProductPrice(product.prices as Price[], bundleID);
  if (bundleID && !(price instanceof BundlePrice && price.bundleID === bundleID))
    logger.warn({ bundleID, productID }, 'IKH bundle price missing; falling back to regular price');
  return price;
};

export const ikhPaymentPlugin: PaymentPlugin<'invoice', 'default'> = {
  id: 'payment::ikh',
  type: 'payment',
  paymentMethods: ['invoice'],
  internalPaymentExportConfig: {
    invoiceReceiverEnum: IKHInvoiceReceiverEnum.IKHINTERNAL,
    costCenterReceiver: '64050208',
    kostenart: '91100100',
  },
  getProductPrice,
  createPayment: async (participationIDs: string[], _paymentMethod) => {
    const user = await getCurrentUserServer();
    if (!user) throw new Error('user not found');

    const participations = await getParticipationsByID(participationIDs)({
      _id: 1,
      userID: 1,
      user: {
        _id: 1,
        email: 1,
        firstName: 1,
        lastName: 1,
        title: 1,
        username: 1,
        whatsappNotification: 1,
        whatsappPhone: 1,
        suspentions: { _id: 1, institutionID: 1 },
        receivesNewsletter: 1,
      },
      productID: 1,
      product: { _id: 1, name: 1, institutionID: 1, hasPrice: 1, prices: { unitAmount: 1, currency: 1 } },
      createdAt: 1,
      updatedAt: 1,
      state: 1,
      isNoShow: 1,
      signoffDate: 1,
      registrationDate: 1,
      fulfilledParticipationRequirements: 1,
      lastStateChangeWasInitiatedByAdmin: 1,
      currentPayment: { status: 1, participationIDs: 1, orderNr: { year: 1, nr: 1 }, amount: 1, discount: 1 },
      productPriceForUser: { unitAmount: 1, currency: 1 },
    });

    const institutionID = participations[0]?.product?.institutionID;
    if (!institutionID) throw new Error('something went wrong');

    return await paymentPluginIkhPay(institutionID, participationIDs)();
  },
  pay: async (paymentID, _paymentMethod) => {
    // nothing to do here; invoice gets paid manually by user
  },
  sendInvoice: async (invoiceID: string) => {
    // NOTE: deactivate invoice for now
    return false;

    await sendIKHInvoiceMails(invoiceID, false)();
  },

  afterEnrollment: async participationID => {},

  cancleEnrollment: async (participationID: string) => {
    const payment = await getParticipationCurrentPaymentServer(participationID);
    if (!payment) return;
    switch (payment.status) {
      case 'paid':
        payment.status = 'refund';
        await saveEntityData(payment);
        return;
      default:
        await defaultPaymentPlugin.cancleEnrollment(participationID);
        return;
    }
  },

  priceGroups: ['default'],
};

export function testIkhPaymentPlugin() {}
